import { motion } from "framer-motion";
import { Leaf as LeafLucide, Shovel, ArrowUpRight } from "lucide-react";
import { SERVICES, IMAGES } from "../../content/site";
import { SectionHead } from "./SectionHead";
import { Leaf } from "./Leaf";
import { EASE } from "./motion";
import { scrollToId } from "./useLenis";
import { Plans } from "./Plans";

const ICONS = { Leaf: LeafLucide, Shovel };
const SPANS = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-6", "md:col-span-6"];

const pick = (key) => {
  window.dispatchEvent(new CustomEvent("gw:service", { detail: key }));
  scrollToId("kontakt");
};

const ImageCard = ({ s, big }) => (
  <div className={`relative h-full overflow-hidden rounded-[28px] ${big ? "min-h-[420px] md:min-h-[560px]" : "min-h-[300px]"}`}>
    <img src={IMAGES[s.image]} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#0D2F18]/90 via-[#0D2F18]/30 to-transparent" />
    <div className="relative flex h-full flex-col justify-end p-7 md:p-9">
      <h3 className={`font-display font-medium text-[#FBF9F4] ${big ? "text-4xl md:text-5xl" : "text-3xl"}`}>{s.title}</h3>
      <p className="mt-3 max-w-md text-[#FBF9F4]/90">{s.text}</p>
      {s.note && <p className="mt-2 text-xs text-[#DCEFBE]">{s.note}</p>}
    </div>
  </div>
);

const IconCard = ({ s, i }) => {
  const Icon = ICONS[s.icon];
  const dark = i === 3;
  return (
    <div className={`flex h-full min-h-[300px] flex-col justify-between rounded-[28px] p-7 md:p-9 ${dark ? "bg-[#1E5631] text-[#FBF9F4]" : "border border-[#E2DDD0] bg-white"}`}>
      <span className={`grid h-14 w-14 place-items-center rounded-2xl ${dark ? "bg-[#DCEFBE] text-[#1E5631]" : "bg-[#F3EFE6] text-[#1E5631]"}`}>
        <Icon className="h-6 w-6 transition-transform duration-500 group-hover:rotate-12" />
      </span>
      <div>
        <h3 className={`font-display text-3xl font-medium ${dark ? "" : "text-[#1B281E]"}`}>{s.title}</h3>
        <p className={`mt-3 ${dark ? "text-[#FBF9F4]/90" : "text-[#4B5B4E]"}`}>{s.text}</p>
        {s.note && <p className={`mt-2 text-xs ${dark ? "text-[#DCEFBE]" : "text-[#8C6D53]"}`}>{s.note}</p>}
      </div>
    </div>
  );
};

export const Services = () => (
  <section id="leistungen" data-testid="services-section" className="relative overflow-hidden bg-[#FBF9F4] py-24 md:py-36">
    <Leaf className="right-[6%] top-24 h-16 w-11" color="#8C6D53" rotate={30} />
    <Leaf className="left-[3%] top-[55%] hidden h-12 w-8 md:block" color="#1E5631" rotate={-25} />
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead
        eyebrow="Leistungen"
        lines={["Was wir für", "Ihren Garten tun."]}
        intro="Ein Überblick über die Arbeiten, die GRÜNWIRBEL in Herxheim und Umgebung übernimmt. Sprechen Sie uns gern an, wenn Sie etwas Bestimmtes vorhaben."
      />
      <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-12 md:gap-6">
        {SERVICES.map((s, i) => (
          <motion.button
            key={s.key}
            type="button"
            data-testid={`service-card-${s.key}`}
            onClick={() => pick(s.title)}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.12 }}
            whileHover={{ y: -6 }}
            className={`group relative text-left ${SPANS[i]}`}
          >
            {s.image ? <ImageCard s={s} big={i === 0} /> : <IconCard s={s} i={i} />}
            <span className="absolute right-5 top-5 grid h-11 w-11 place-items-center rounded-full bg-[#FBF9F4] text-[#1E5631] opacity-90 transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-5 w-5" />
            </span>
          </motion.button>
        ))}
      </div>
      <Plans />
    </div>
  </section>
);
